const UTM_STORAGE_KEY = "dosha_quiz_utm_attribution";
const LANDING_URL_KEY = "dosha_quiz_landing_url";

const UTM_PARAM_KEYS = [
  "gclid",
  "utm_source",
  "utm_medium",
  "campaignid",
  "adgroupid",
  "keywordid",
  "matchtype",
  "assetid",
  "device",
  "locationid",
];

const CLIENT_CONTEXT_KEYS = [
  "user_agent",
  "utm_data",
  "location",
  "latitude",
  "longitude",
];

const hasValue = (value) =>
  value != null && String(value).trim() !== "";

export const sanitizeAttribution = (attribution = {}) => {
  const sanitized = {};

  Object.entries(attribution).forEach(([key, value]) => {
    if (!hasValue(value) && typeof value !== "number") {
      return;
    }

    sanitized[key] = typeof value === "string" ? value.trim() : value;
  });

  return sanitized;
};

const mergeAttribution = (...parts) => {
  const merged = {};

  parts.forEach((part) => {
    Object.assign(merged, sanitizeAttribution(part));
  });

  return sanitizeAttribution(merged);
};

const getBrowserContext = () => {
  const context = {};

  if (typeof navigator !== "undefined" && hasValue(navigator.userAgent)) {
    context.user_agent = navigator.userAgent;
  }

  return context;
};

const parseAttributionFromUrl = (url) => {
  if (!url) {
    return {};
  }

  try {
    const params = new URLSearchParams(new URL(url).search);
    const attribution = {};

    UTM_PARAM_KEYS.forEach((key) => {
      const value = params.get(key);
      if (hasValue(value)) {
        attribution[key] = value.trim();
      }
    });

    const hasMarketingParams =
      UTM_PARAM_KEYS.some((key) => hasValue(params.get(key))) ||
      new URL(url).search.includes("utm_");

    if (hasMarketingParams) {
      attribution.utm_data = url;
    }

    return attribution;
  } catch {
    return {};
  }
};

const readStoredAttribution = () => {
  try {
    const stored = sessionStorage.getItem(UTM_STORAGE_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch {
    return {};
  }
};

const persistAttribution = (attribution) => {
  sessionStorage.setItem(UTM_STORAGE_KEY, JSON.stringify(attribution));
};

export const captureUtmAttribution = () => {
  const currentUrl = window.location.href;

  if (!sessionStorage.getItem(LANDING_URL_KEY)) {
    sessionStorage.setItem(LANDING_URL_KEY, currentUrl);
  }

  const landingUrl = sessionStorage.getItem(LANDING_URL_KEY) || currentUrl;
  const parsedLanding = parseAttributionFromUrl(landingUrl);
  const parsedCurrent = parseAttributionFromUrl(currentUrl);

  const attribution = mergeAttribution(
    readStoredAttribution(),
    parsedLanding,
    parsedCurrent,
    getBrowserContext(),
    {
      utm_data: parsedLanding.utm_data || parsedCurrent.utm_data || landingUrl,
    }
  );

  persistAttribution(attribution);
  return attribution;
};

export const captureLocationAsync = () => {
  if (typeof navigator === "undefined" || !navigator.geolocation) {
    return Promise.resolve(getUtmAttribution());
  }

  return new Promise((resolve) => {
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const attribution = mergeAttribution(getUtmAttribution(), {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        });
        persistAttribution(attribution);
        resolve(attribution);
      },
      () => resolve(getUtmAttribution()),
      {
        enableHighAccuracy: false,
        maximumAge: 300000,
        timeout: 8000,
      }
    );
  });
};

export const getUtmAttribution = () => {
  return mergeAttribution(readStoredAttribution(), getBrowserContext());
};

const extractAttributionFromSource = (source) => {
  if (!source || typeof source !== "object") {
    return {};
  }

  if (source.attribution && typeof source.attribution === "object") {
    return sanitizeAttribution(source.attribution);
  }

  const attribution = {};

  [...UTM_PARAM_KEYS, ...CLIENT_CONTEXT_KEYS].forEach((key) => {
    if (hasValue(source[key]) || typeof source[key] === "number") {
      attribution[key] =
        typeof source[key] === "string" ? source[key].trim() : source[key];
    }
  });

  return attribution;
};

export const getUtmAttributionFromSources = (...sources) => {
  const mergedParts = [readStoredAttribution(), getBrowserContext()];

  sources.forEach((source) => {
    mergedParts.push(extractAttributionFromSource(source));
  });

  const merged = mergeAttribution(...mergedParts);

  if (Object.keys(merged).length > 0) {
    return merged;
  }

  return captureUtmAttribution();
};

export const buildAttributionPayload = (...sources) => {
  const attribution = getUtmAttributionFromSources(...sources);
  return Object.keys(attribution).length > 0 ? { attribution } : {};
};
